type LatLng = { lat: number; lng: number };

const R = 6371;

function distanceKm(a: LatLng, b: LatLng) {
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

const finite = (value: any): number | null => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const clamp01 = (value: any) => Math.max(0, Math.min(1, Number(value) || 0));

const cleanStrings = (values: any[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values || []) {
    const text = String(value ?? '').trim();
    const key = text.toLowerCase();
    if (!text || seen.has(key)) continue;
    seen.add(key);
    out.push(text);
  }
  return out;
};

const currentStrength = (speedKnots: number | null): 'slack' | 'light' | 'moderate' | 'strong' | 'unknown' => {
  if (speedKnots == null) return 'unknown';
  if (speedKnots <= 0.05) return 'slack';
  if (speedKnots < 0.3) return 'light';
  if (speedKnots < 0.75) return 'moderate';
  return 'strong';
};


const lowerText = (value: any) => String(value ?? '').toLowerCase();

const explicitCloudPct = (value: any): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.max(0, Math.min(100, value));
  if (value && typeof value === 'object') {
    for (const key of ['percent', 'pct', 'coverage', 'cloudCover', 'clouds']) {
      const n = finite(value?.[key]);
      if (n != null) return Math.max(0, Math.min(100, n));
    }
  }
  const match = String(value ?? '').match(/(\d{1,3})\s*%/);
  return match ? Math.max(0, Math.min(100, Number(match[1]))) : null;
};

const explicitWindMph = (value: any): number | null => {
  if (!value || typeof value !== 'object') return null;
  const directMph = finite(value?.mph ?? value?.speedMph ?? value?.windMph);
  if (directMph != null) return directMph;
  const kph = finite(value?.kph ?? value?.speedKph ?? value?.windKph);
  if (kph != null) return kph * 0.621371;
  const ms = finite(value?.mps ?? value?.ms ?? value?.speedMs ?? value?.speedMps);
  if (ms != null) return ms * 2.23694;
  const knots = finite(value?.knots ?? value?.kt ?? value?.speedKnots);
  if (knots != null) return knots * 1.15078;
  return null;
};

const buildWeatherSignal = (currentWeather: any) => {
  const conditionText = lowerText(currentWeather?.condition);
  const cloudsText = lowerText(currentWeather?.clouds);
  const windText = lowerText(currentWeather?.wind);
  const precipText = lowerText(currentWeather?.precipitation);
  const visibilityText = lowerText(currentWeather?.visibility);
  const combined = [conditionText, cloudsText, precipText, visibilityText].filter(Boolean).join(' ');

  const cloudPct = explicitCloudPct(currentWeather?.clouds);
  const cloudClass: 'clear' | 'mixed' | 'overcast' | 'unknown' =
    cloudPct != null
      ? cloudPct >= 70 ? 'overcast' : cloudPct <= 25 ? 'clear' : 'mixed'
      : /overcast|cloudy|mostly cloudy|heavy cloud/.test(combined)
        ? 'overcast'
        : /clear|sunny|mostly clear/.test(combined)
          ? 'clear'
          : /partly|scattered|broken cloud/.test(combined)
            ? 'mixed'
            : 'unknown';

  const precipitationActive =
    /rain|drizzle|shower|storm|snow|sleet/.test(combined) ||
    (finite(currentWeather?.precipitation?.amount ?? currentWeather?.precipitation?.mm ?? currentWeather?.precipitation) ?? 0) > 0;

  const visibilityReduced = /fog|mist|haze|smoke/.test(combined);

  const windMph = explicitWindMph(currentWeather?.wind);
  const windClass: 'calm' | 'breezy' | 'strong' | 'unknown' =
    windMph != null
      ? windMph >= 20 ? 'strong' : windMph >= 7 ? 'breezy' : 'calm'
      : /gale|strong wind|very windy|high wind/.test(windText)
        ? 'strong'
        : /breezy|windy|moderate wind/.test(windText)
          ? 'breezy'
          : /calm|light wind|light breeze/.test(windText)
            ? 'calm'
            : 'unknown';

  const lowLight =
    cloudClass === 'overcast' ||
    precipitationActive ||
    visibilityReduced ||
    /dusk|dawn|night/.test(conditionText);

  const summaryParts = [
    lowLight ? 'reduced light' : cloudClass === 'clear' ? 'bright/clear' : '',
    windClass === 'strong' ? 'strong wind' : windClass === 'breezy' ? 'working breeze' : '',
    precipitationActive ? 'precipitation' : '',
    visibilityReduced ? 'reduced visibility' : '',
  ].filter(Boolean);

  return {
    lowLight,
    precipitationActive,
    visibilityReduced,
    windClass,
    cloudClass,
    ...(summaryParts.length ? { summary: summaryParts.join(' · ') } : {}),
  };
};

const sourceLabelForCurrent = (current: any) =>
  String(
    current?.sourceStationName ||
      current?.sourceModelName ||
      current?.sourceModelId ||
      '',
  ).trim();

const evidenceKindWeight = (value: any) => {
  const kind = String(value || '').toLowerCase();
  if (kind === 'observed') return 1;
  if (kind === 'predicted') return 0.96;
  if (kind === 'modeled') return 0.91;
  if (kind === 'interpolated') return 0.88;
  if (kind === 'derived') return 0.84;
  if (kind === 'inferred') return 0.72;
  return 0.78;
};

const qualityAdjustedConfidence = (read: any, kind: 'current' | 'bathymetry') => {
  if (read?.available !== true) return 0;
  const q = read?.quality || {};
  let score = clamp01(q?.confidence);
  if (!score) score = kind === 'current' ? 0.5 : 0.55;

  score *= evidenceKindWeight(q?.evidenceKind);

  if (q?.stale === true) score *= 0.66;

  const sourceDistanceKm = finite(q?.sourceDistanceKm);
  if (kind === 'current' && sourceDistanceKm != null) {
    if (sourceDistanceKm > 30) score *= 0.72;
    else if (sourceDistanceKm > 20) score *= 0.84;
    else if (sourceDistanceKm > 10) score *= 0.93;
  }

  const resolutionM = finite(q?.horizontalResolutionM);
  if (resolutionM != null) {
    if (resolutionM > 10000) score *= 0.76;
    else if (resolutionM > 5000) score *= 0.84;
    else if (resolutionM > 1500) score *= 0.92;
  }

  return clamp01(score);
};

export type FishIQEnvironmentalContext = {
  anchor: LatLng;
  sourceDistanceKm?: number;
  environmentConfidence: number;
  evidenceCount: number;
  evidenceStrength: 'strong' | 'good' | 'limited' | 'unavailable';
  qualitySummary?: string;
  current?: {
    available: boolean;
    speedKnots?: number;
    directionDeg?: number;
    directionLabel?: string;
    strength: 'slack' | 'light' | 'moderate' | 'strong' | 'unknown';
    phase?: string;
    trend?: string;
    confidence: number;
    rawConfidence?: number;
    evidenceKind?: string;
    qualityLabel?: string;
    stale?: boolean;
    sourceDistanceKm?: number;
    horizontalResolutionM?: number;
    sourceLabel?: string;
  };
  bathymetry?: {
    available: boolean;
    depthMeters?: number;
    nearbyMinMeters?: number;
    nearbyMaxMeters?: number;
    localReliefMeters?: number;
    slopeDeg?: number;
    slopeClass?: string;
    structureTypes: string[];
    confidence: number;
    rawConfidence?: number;
    evidenceKind?: string;
    qualityLabel?: string;
    stale?: boolean;
    sourceDistanceKm?: number;
    horizontalResolutionM?: number;
  };
  weather?: {
    temp?: any;
    condition?: any;
    wind?: any;
    pressure?: any;
    clouds?: any;
    visibility?: any;
    precipitation?: any;
  };
  weatherSignal?: {
    lowLight: boolean;
    precipitationActive: boolean;
    visibilityReduced: boolean;
    windClass: 'calm' | 'breezy' | 'strong' | 'unknown';
    cloudClass: 'clear' | 'mixed' | 'overcast' | 'unknown';
    summary?: string;
  };
  light?: {
    moonPhase?: any;
    sunrise?: any;
    sunset?: any;
  };
  tides?: {
    stationName?: any;
    nextHigh?: any;
    nextLow?: any;
  };
  marine?: {
    waveHeight?: any;
    waveDirection?: any;
    wavePeriod?: any;
  };
  provider?: any;
  fetchedAt?: any;
  generatedAtMs?: number;
  provenance: Array<{
    provider?: string;
    dataset?: string;
    sourceId?: string;
    evidenceKind?: string;
    confidence?: number;
  }>;
};

/**
 * Normalizes nearby weather + exact-anchor Water Intel into a FishIQ-safe
 * environmental context. D.7 carries Water Intel quality forward instead of
 * flattening observed/model/interpolated reads into the same trust score.
 */
export function buildWaterIntelEnvironment(
  weatherData: any,
  anchor: LatLng | null,
  waterIntelRead?: any | null,
): FishIQEnvironmentalContext | null {
  if (!anchor) return null;

  let localWeather: any = null;
  let sourceDistanceKm: number | undefined;

  if (weatherData) {
    const lat = finite(weatherData?.coords?.lat);
    const lng = finite(weatherData?.coords?.lon ?? weatherData?.coords?.lng);
    if (lat != null && lng != null) {
      const distance = distanceKm(anchor, { lat, lng });
      if (distance <= 35) {
        localWeather = weatherData;
        sourceDistanceKm = distance;
      }
    }
  }

  const currentRead = waterIntelRead?.currents || null;
  const bathymetryRead = waterIntelRead?.bathymetry || null;
  const currentAvailable = currentRead?.available === true;
  const bathymetryAvailable = bathymetryRead?.available === true;

  if (!localWeather && !currentAvailable && !bathymetryAvailable) return null;

  const currentRawConfidence = currentAvailable ? clamp01(currentRead?.quality?.confidence) : 0;
  const bathymetryRawConfidence = bathymetryAvailable ? clamp01(bathymetryRead?.quality?.confidence) : 0;
  const currentConfidence = qualityAdjustedConfidence(currentRead, 'current');
  const bathymetryConfidence = qualityAdjustedConfidence(bathymetryRead, 'bathymetry');
  const weatherConfidence = localWeather ? 0.52 : 0;

  const weighted: Array<{ value: number; weight: number }> = [];
  if (currentAvailable) weighted.push({ value: currentConfidence, weight: 0.48 });
  if (bathymetryAvailable) weighted.push({ value: bathymetryConfidence, weight: 0.37 });
  if (localWeather) weighted.push({ value: weatherConfidence, weight: 0.15 });

  const weightSum = weighted.reduce((sum, row) => sum + row.weight, 0);
  const environmentConfidence = weightSum > 0
    ? weighted.reduce((sum, row) => sum + row.value * row.weight, 0) / weightSum
    : 0;

  const evidenceCount = weighted.length;
  const evidenceStrength =
    environmentConfidence >= 0.76 && evidenceCount >= 2
      ? 'strong'
      : environmentConfidence >= 0.58
        ? 'good'
        : environmentConfidence > 0
          ? 'limited'
          : 'unavailable';

  const qualitySummary = evidenceStrength === 'strong'
    ? 'Multiple good-quality live environmental signals'
    : evidenceStrength === 'good'
      ? 'Useful environmental evidence with normal model limits'
      : evidenceStrength === 'limited'
        ? 'Environmental evidence is partial or lower-confidence'
        : 'No environmental evidence available';

  const currentSpeed = finite(currentRead?.speedKnots);
  const currentDirection = finite(currentRead?.directionDeg);
  const depth = finite(bathymetryRead?.depthMeters);
  const nearbyMin = finite(bathymetryRead?.nearbyMinMeters);
  const nearbyMax = finite(bathymetryRead?.nearbyMaxMeters);
  const relief = finite(bathymetryRead?.localReliefMeters);
  const slope = finite(bathymetryRead?.slopeDeg);

  const current = currentRead
    ? {
        available: currentAvailable,
        ...(currentSpeed != null ? { speedKnots: currentSpeed } : {}),
        ...(currentDirection != null ? { directionDeg: currentDirection } : {}),
        directionLabel: String(currentRead?.directionLabel || '').trim() || undefined,
        strength: currentStrength(currentSpeed),
        phase: String(currentRead?.phase || '').trim() || undefined,
        trend: String(currentRead?.trend || '').trim() || undefined,
        confidence: currentConfidence,
        rawConfidence: currentRawConfidence,
        evidenceKind: String(currentRead?.quality?.evidenceKind || '').trim() || undefined,
        qualityLabel: String(currentRead?.quality?.label || '').trim() || undefined,
        stale: currentRead?.quality?.stale === true,
        ...(finite(currentRead?.quality?.sourceDistanceKm) != null ? { sourceDistanceKm: Number(currentRead.quality.sourceDistanceKm) } : {}),
        ...(finite(currentRead?.quality?.horizontalResolutionM) != null ? { horizontalResolutionM: Number(currentRead.quality.horizontalResolutionM) } : {}),
        sourceLabel: sourceLabelForCurrent(currentRead) || undefined,
      }
    : undefined;

  const bathymetry = bathymetryRead
    ? {
        available: bathymetryAvailable,
        ...(depth != null ? { depthMeters: depth } : {}),
        ...(nearbyMin != null ? { nearbyMinMeters: nearbyMin } : {}),
        ...(nearbyMax != null ? { nearbyMaxMeters: nearbyMax } : {}),
        ...(relief != null ? { localReliefMeters: relief } : {}),
        ...(slope != null ? { slopeDeg: slope } : {}),
        slopeClass: String(bathymetryRead?.slopeClass || '').trim() || undefined,
        structureTypes: cleanStrings(
          Array.isArray(bathymetryRead?.structureTypes)
            ? bathymetryRead.structureTypes.filter((value: any) => value && value !== 'unknown')
            : [],
        ),
        confidence: bathymetryConfidence,
        rawConfidence: bathymetryRawConfidence,
        evidenceKind: String(bathymetryRead?.quality?.evidenceKind || '').trim() || undefined,
        qualityLabel: String(bathymetryRead?.quality?.label || '').trim() || undefined,
        stale: bathymetryRead?.quality?.stale === true,
        ...(finite(bathymetryRead?.quality?.sourceDistanceKm) != null ? { sourceDistanceKm: Number(bathymetryRead.quality.sourceDistanceKm) } : {}),
        ...(finite(bathymetryRead?.quality?.horizontalResolutionM) != null ? { horizontalResolutionM: Number(bathymetryRead.quality.horizontalResolutionM) } : {}),
      }
    : undefined;

  const currentWeather = localWeather?.current || {};
  const tides = localWeather?.tides || {};
  const marine = localWeather?.marine || {};
  const weatherSignal = localWeather ? buildWeatherSignal(currentWeather) : undefined;

  const provenance = Array.isArray(waterIntelRead?.provenance)
    ? waterIntelRead.provenance.map((row: any) => ({
        provider: row?.provider,
        dataset: row?.dataset,
        sourceId: row?.sourceId,
        evidenceKind: row?.evidenceKind,
      }))
    : [];

  if (localWeather) {
    provenance.push({
      provider: String(localWeather?.provider ?? localWeather?.source ?? 'weather').trim() || 'weather',
      evidenceKind: 'inferred',
      confidence: weatherConfidence,
    });
  }

  return {
    anchor: { lat: Number(anchor.lat), lng: Number(anchor.lng) },
    ...(sourceDistanceKm != null ? { sourceDistanceKm } : {}),
    environmentConfidence,
    evidenceCount,
    evidenceStrength,
    qualitySummary,
    current,
    bathymetry,
    ...(localWeather
      ? {
          weather: {
            temp: currentWeather?.temp,
            condition: currentWeather?.condition,
            wind: currentWeather?.wind,
            pressure: currentWeather?.pressure,
            clouds: currentWeather?.clouds,
            visibility: currentWeather?.visibility,
            precipitation: currentWeather?.precipitation,
          },
          weatherSignal,
          light: {
            moonPhase:
              localWeather?.moonPhase ??
              localWeather?.astronomy?.moonPhase ??
              currentWeather?.moonPhase,
            sunrise:
              localWeather?.sunrise ??
              localWeather?.astronomy?.sunrise ??
              currentWeather?.sunrise,
            sunset:
              localWeather?.sunset ??
              localWeather?.astronomy?.sunset ??
              currentWeather?.sunset,
          },
          tides: {
            stationName: tides?.stationName,
            nextHigh: tides?.nextHigh ?? currentWeather?.nextHighTide,
            nextLow: tides?.nextLow ?? currentWeather?.nextLowTide,
          },
          marine: {
            waveHeight: marine?.waveHeight,
            waveDirection: marine?.waveDirection,
            wavePeriod: marine?.wavePeriod,
          },
          provider: localWeather?.provider ?? localWeather?.source,
          fetchedAt: localWeather?.fetchedAt,
        }
      : {}),
    generatedAtMs: Number(waterIntelRead?.generatedAtMs || Date.now()),
    provenance,
  };
}
